import { DutyItem, RosterType } from '@app/Models/WebApiModels';

import { Sizes } from '@app/AppConstants/Sizes';
import { SelectionStrategyConstructor } from './SelectionStrategies/BaseRosterSelectionStrategy';
import { Range } from './Stores/Range';
import { IPersonStore } from './Stores/IPersonStore';
import { RosterViewState } from './RosterViewState';

import { DutyLineRenderExtrasCallback, DoubleClickCallback, DutyLineRenderDetailsCallback } from '@app/Components/RosterLine';
import { LicensesClickHandler, PersonListRenderExtraFiguresCallback } from '../PersonsListItem';
import { DrawDayCssClassNameHandler, DrawDayExtensionsHandler, DrawMonthExtensionsHandler } from './Components';
import HistorizationStore from '@app/Stores/HistorizationStore';
import { HomeFiltersState } from '@app/Pages/Home/Models';

export interface IVirtualizedRoster {
    selectedRowIndex: number | null;
    selectedColumnIndex: number | null;
    selectedRowNumber: number | null;
    datesRange: number;
    selectedCells: RosterElement[];
    cellWidthPx: number;
    setFocusedElement: (element: RosterElement) => void;
}

export type DrawElementOptions = {
    viewportDays: Date[];
    virtualHeight: number;
    virtualWidth: number;
    viewportTop: number;
    viewportLeft: number;
};

export type DrawChildElementOptions = DrawElementOptions & {
    ref: React.RefObject<HTMLElement>;
};

export enum NavigateBehavior {
    center,
    topLeft
}

export type DrawElementHandler = (roster: IVirtualizedRoster, options: DrawElementOptions) => JSX.Element | null;
export type DrawChildElementHandler = (roster: IVirtualizedRoster, options: DrawChildElementOptions) => JSX.Element | null;

export type RefHandler = (ref: React.RefObject<HTMLDivElement>) => void;

export type RosterElement = {
    personId: string;
    dateUtc: number;
};

export enum SelectionMode {
    NormalMultiSelect,
    SingleRange,
    StickyDayRange
}

export enum ActionInitiator {
    Code,
    Keyboard,
    DragCopy,
    DragMove
}

export enum RosterZoom {
    x1,
    x2,
    x4,
    x8,
    x16
}

export type DragEffect = 'move' | 'copy' | 'none';

type CellFocusedHandler = (element: RosterElement | null) => void;
type CellSelectedHandler = (elements: RosterElement[]) => void;
type ScrollHandler = (scrollTop: number, scrollLeft: number, saveConfig?: boolean) => void;
export type CanCopyHandler = (initiator: ActionInitiator | null, duties: DutyItem[]) => boolean;
type PasteDutyHandler = (initiator: ActionInitiator, duties: DutyItem[]) => void;
type DateRangeLoadHandler = (viewportRange: Range<Date> | null, fullRange: Range<Date> | null, loadMode: LoadMode, refreshOptions?: { range: Range<Date>, personIds: string[], isValidation: boolean, functionGroupId?: string, functionId?: string }) => void;
type PersonRangeLoadHandler = (viewportRange: Range<number> | null, refreshPersonIds: string[] | null, loadMode: LoadMode) => void;

export type VirtualizedRosterProps = {
    className: string;
    hidden?: boolean;

    type: RosterType;

    personsStore: IPersonStore;
    viewState: RosterViewState;
    historizationStore?: HistorizationStore;

    filterState?: HomeFiltersState | null;

    allowedDateRange?: Array<Range<Date>> | Range<Date> | null;
    showCapacityNumbers?: boolean;

    personInfoWidthPx?: number;
    bottomInfoHeightPx?: number;
    lineInfoHeight?: number;
    initialZoom?: RosterZoom;
    showZoomControls?: boolean;
    noWrapper?: boolean;

    showPersonList?: boolean;
    showFocusedPersonDay?: boolean;
    selectionMode?: SelectionMode;
    selectionStrategy?: SelectionStrategyConstructor;
    autoExpandPeriod?: boolean;
    showSwissTime?: boolean;
    highlightMajorMonth?: boolean;
    jeppesenFileUri?: string;
    generatedPlanFileUri?: string;

    onStartDragDuty?: (sourceElement: Element | null, isAllowCopying?: boolean) => boolean;
    onDragOverDuty?: (targetElement: Element, isCopy: boolean) => DragEffect;
    onDragEndDuty?: () => void;
    onDropDuty?: (targetElement: Element, isCopy: boolean) => void;

    onRenderHeader?: DrawElementHandler;
    onRenderFooter?: DrawElementHandler;
    onRenderPersonList?: DrawChildElementHandler;

    onRenderCustomDate?: DrawDayExtensionsHandler;
    onRenderDayExtensions?: DrawDayExtensionsHandler;
    onRenderDayCssClassName?: DrawDayCssClassNameHandler;
    onRenderMonthExtensions?: DrawMonthExtensionsHandler;
    onRenderDailyFigures?: DrawDayExtensionsHandler;
    onRenderRosterFilter?: DrawDayExtensionsHandler;
    onRenderDaysListExtensions?: (datesListRef: React.RefObject<HTMLDivElement>) => JSX.Element | null;

    onRenderCellExtras?: DutyLineRenderExtrasCallback;
    onRenderCellDetails?: DutyLineRenderDetailsCallback;

    onRenderPersonExtraInfo?: PersonListRenderExtraFiguresCallback;
    onRenderGridExtras?: DrawChildElementHandler;
    onPersonLicenseClick?: LicensesClickHandler;

    onCellFocused?: CellFocusedHandler;
    onCellSelected?: CellSelectedHandler;
    onScroll?: ScrollHandler;

    //TODO: START: review later
    height?: number;
    //TODO: END

    onCanCopyDuties?: CanCopyHandler;
    onPasteDuties?: PasteDutyHandler;
    onCellDoubleClick?: DoubleClickCallback;

    onMajorMonthChange?: (day: Date) => void;
    onSaveViewState?: (viewState: RosterViewState) => void;
    onDateRangeLoad?: DateRangeLoadHandler;
    onPersonRangeLoad?: PersonRangeLoadHandler;
    onPlanningDutyPeriodStatusLoad?: () => void;
    onZoom?: (e: RosterZoom) => void;
    onNavigateToPerson?: (personId: string, date: Date) => Promise<void>;

    gridRef?: RefHandler;
};

export type StoredPersons = {
    currentIndex: number;
    personIds: Array<string>
};

export enum LoadMode {
    Viewport,
    Refresh,
    Clean,
    RefreshVisible
}

export type RefreshRosterLineDto = {
    personId?: string;
    hasDutyChanges: boolean;
    dateFromUtc: Date | null;
    dateToUtc: Date | null;
    isValidation: boolean;
    functionGroupId?: string;
    functionId?: string;
};

export const getZoomMultiplier = (zoom: RosterZoom) => {
    switch (zoom) {
        case RosterZoom.x1:
            return 8;
        case RosterZoom.x2:
            return 4;
        case RosterZoom.x4:
            return 2;
        case RosterZoom.x8:
            return 1;
        default:
            throw new Error(`Zoom ${zoom} is not supported`);
    }
};

export const getDayWidth = (zoom: RosterZoom) => {
    return Sizes.dutyWidths * getZoomMultiplier(zoom);
};

export * from './Stores/IPersonStore';
export * from './Stores/Range';
export * from './Stores/RangeStore';
export * from './Stores/SinglePersonStore';
export * from './RosterViewState';
